import { createSelector } from "@reduxjs/toolkit";
import { AuthToken, InfoResponse, SelfEntityModel } from "kz-ui-sdk";
import moment from "moment";

import type { RootState } from "@stores";
import { AuthStatus, UserMode } from "@types";
import logger from "@utils/logger";

function computeAuthStatus(
  oauth: AuthToken | undefined | null,
  self: SelfEntityModel | undefined | null,
  serverInfo: InfoResponse | undefined | null,
): AuthStatus {
  // Token not found, authenticating state
  if (oauth === undefined) {
    logger._console.log("[Auth] Token not found, authenticating...");
    return AuthStatus.Authenticating;
  }
  // Found token, but no profile, unauthenticated state
  // Can be used to redirect to login page
  if (!self || !oauth) {
    logger._console.log("[Auth] Token found, but no profile, Unauthenticated.", { self }, { oauth });
    return AuthStatus.Unauthenticated;
  }
  // Found token and profile, authenticated state
  logger._console.log("[Auth] Token and profile found, Authenticated.", { self }, { oauth });
  // Check if user needs to reset password
  if (self?.member?.pwExpireAt) {
    logger._console.log("[Auth] Found pwExpiredAt.", { self }, { oauth });
    const expiredAt = moment.utc(self?.member?.pwExpireAt).utcOffset(serverInfo?.utcOffset ?? 0);
    logger._console.log("[Auth] Found pwExpiredAt.", expiredAt.format(), moment().format());
    if (expiredAt.isBefore(moment())) {
      return AuthStatus.PasswordExpired;
    }
  }
  return AuthStatus.Authenticated;
}

export const selectAuthStatus = createSelector(
  (state: RootState) => state.auth.oauth,
  (state: RootState) => state.auth.self,
  (state: RootState) => state.preferences.serverInfo,
  computeAuthStatus,
);

// Narrow alternative to useAuthSession, which subscribes to the whole auth slice.
export const selectUserMode = createSelector(selectAuthStatus, (status) =>
  status === AuthStatus.Authenticated ? UserMode.MEMBER : UserMode.GUEST,
);
